import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
  increment,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import { FIRST_KICKOFF_UTC, getNow } from '../utils/lockUtils';

export const DEFAULT_POINTS_CONFIG = {
  group: 5,
  r32: 10,
  r16: 15,
  qf: 20,
  sf: 25,
  final: 50,
  third: 50,
};

function generateInviteCode() {
  return crypto.randomUUID();
}

/**
 * Create a new league and auto-join the creator as the first member.
 * Returns the new leagueId.
 */
export async function createLeague(commissionerId, { name, isPublic, pointsConfig = {} }) {
  const trimmedName = name.trim();
  if (!trimmedName || trimmedName.length > 60) {
    throw new Error('League name must be between 1 and 60 characters.');
  }
  const leagueId = doc(collection(db, 'leagues')).id;
  const inviteCode = generateInviteCode();
  const resolvedConfig = { ...DEFAULT_POINTS_CONFIG, ...pointsConfig };

  await setDoc(doc(db, 'leagues', leagueId), {
    name: trimmedName,
    commissionerId,
    isPublic,
    createdAt: serverTimestamp(),
    memberCount: 1,
    settingsLocked: false,
    pointsConfig: resolvedConfig,
    inviteCode,
    isSandbox: false,
  });

  // Creator auto-joins as first member
  await setDoc(doc(db, 'leagueMembers', `${leagueId}_${commissionerId}`), {
    leagueId,
    userId: commissionerId,
    joinedAt: serverTimestamp(),
    totalPoints: 0,
    mpp: 0,
    correctPicks: 0,
    totalPicks: 0,
  });

  return leagueId;
}

/**
 * Join a league by leagueId. No-op if already a member.
 */
export async function joinLeague(leagueId, uid) {
  const memberRef = doc(db, 'leagueMembers', `${leagueId}_${uid}`);
  const existing = await getDoc(memberRef);
  if (existing.exists()) return leagueId; // already a member

  await setDoc(memberRef, {
    leagueId,
    userId: uid,
    joinedAt: serverTimestamp(),
    totalPoints: 0,
    mpp: 0,
    correctPicks: 0,
    totalPicks: 0,
  });

  try {
    await updateDoc(doc(db, 'leagues', leagueId), {
      memberCount: increment(1),
    });
  } catch (_) {
    // memberCount is a cached count; non-fatal if it fails to update
  }

  return leagueId;
}

/**
 * Join a private league via its inviteCode.
 * Returns the leagueId on success.
 * Throws 'EXPIRED' if first kickoff has passed, or 'INVALID' if code not found.
 */
export async function joinLeagueByInviteCode(inviteCode, uid) {
  if (getNow() >= FIRST_KICKOFF_UTC.getTime()) {
    throw new Error('EXPIRED');
  }
  const q = query(collection(db, 'leagues'), where('inviteCode', '==', inviteCode));
  const snap = await getDocs(q);
  if (snap.empty) throw new Error('INVALID');
  const leagueId = snap.docs[0].id;
  await joinLeague(leagueId, uid);
  return leagueId;
}

export async function getLeague(leagueId) {
  const snap = await getDoc(doc(db, 'leagues', leagueId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Get all leagues a user belongs to, with their member stats attached.
 */
export async function getUserLeagues(uid) {
  const q = query(collection(db, 'leagueMembers'), where('userId', '==', uid));
  const snap = await getDocs(q);
  if (snap.empty) return [];

  const membersByLeague = Object.fromEntries(
    snap.docs.map((d) => [d.data().leagueId, d.data()])
  );
  const leagueIds = Object.keys(membersByLeague);

  const leagues = await Promise.all(leagueIds.map(getLeague));
  return leagues
    .filter(Boolean)
    .map((league) => ({ ...league, memberStats: membersByLeague[league.id] }));
}

/**
 * Get all public, non-sandbox leagues sorted by member count desc.
 */
export async function getPublicLeagues() {
  const q = query(
    collection(db, 'leagues'),
    where('isPublic', '==', true),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((l) => !l.isSandbox)
    .sort((a, b) => {
      const diff = (b.memberCount || 0) - (a.memberCount || 0);
      if (diff !== 0) return diff;
      return (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0);
    });
}

export async function updateLeague(leagueId, data) {
  await updateDoc(doc(db, 'leagues', leagueId), data);
}

export async function isLeagueMember(leagueId, uid) {
  const snap = await getDoc(doc(db, 'leagueMembers', `${leagueId}_${uid}`));
  return snap.exists();
}

/**
 * Permanently delete a league and all associated data.
 * Only callable by the commissioner — verified server-side via Firestore rules.
 */
export async function deleteLeague(leagueId) {
  // Delete all picks for this league (batch, capped at 400 per batch)
  const picksSnap = await getDocs(
    query(collection(db, 'picks'), where('leagueId', '==', leagueId))
  );
  for (let i = 0; i < picksSnap.docs.length; i += 400) {
    const batch = writeBatch(db);
    picksSnap.docs.slice(i, i + 400).forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }

  // Delete all leagueMembers for this league
  const membersSnap = await getDocs(
    query(collection(db, 'leagueMembers'), where('leagueId', '==', leagueId))
  );
  if (!membersSnap.empty) {
    const batch = writeBatch(db);
    membersSnap.docs.forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }

  // Delete the league doc itself
  await deleteDoc(doc(db, 'leagues', leagueId));
}

export async function rotateInviteCode(leagueId) {
  const newCode = generateInviteCode();
  await updateDoc(doc(db, 'leagues', leagueId), { inviteCode: newCode });
  return newCode;
}

export async function getLeagueMembers(leagueId) {
  const q = query(collection(db, 'leagueMembers'), where('leagueId', '==', leagueId));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Return the user's rank (1-indexed) in a league by totalPoints.
 * Shared rank for ties — no tiebreaker per spec.
 */
export async function getUserRankInLeague(leagueId, uid) {
  const q = query(collection(db, 'leagueMembers'), where('leagueId', '==', leagueId));
  const snap = await getDocs(q);
  const members = snap.docs.map((d) => d.data());
  const userMember = members.find((m) => m.userId === uid);
  if (!userMember) return null;
  const userPoints = userMember.totalPoints || 0;
  // Number of members with strictly more points + 1
  return members.filter((m) => (m.totalPoints || 0) > userPoints).length + 1;
}
